import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import {
    MORE_GRAPH_OPTIONS_VITAL,
    PLETH_OBJ,
    RESP_OBJ,
    USER_VIEW,
} from "../../common/constant/common-constants";
import {
    setMonitorEcgWaveformStream,
    setMonitorSecondaryWaveformStream,
} from "../../socket/socket";
import DataQueue from "../../common/utils/DataQueue";
import MonitorChartDesktopView from "./MonitorChartDesktopView";
import MonitorChartMobileView from "./MonitorChartMobileView";


const ECG_I_LEAD_ID = MORE_GRAPH_OPTIONS_VITAL.MONITOR[1].id;


const MonitorChart = (props) => {
    const [bedId, setBedId] = useState(null);
    const [secondvitalGraphName, setSecondVitalGraphName] = useState(
        MORE_GRAPH_OPTIONS_VITAL.MONITOR[1]
    );
    const [secondMoreVitalECG, setSecondMoreVitalECG] = useState(false);

    const ecgIQueue = useRef(new DataQueue());
    const ecgIIQueue = useRef(new DataQueue());
    const respQueue = useRef(new DataQueue());
    const plethQueue = useRef(new DataQueue());

    const canvasSize = useRef({ width: 1000, height: 150 });
    const graphDrawerRef = useRef({
        ecgI: null,
        ecgII: null,
        resp: null,
        pleth: null,
    });

    const dataRange = useRef({
        current: {
            ecgI: { min: -100, max: 100 },
            ecgII: { min: -100, max: 100 },
            resp: { min: -140, max: 140 },
            pleth: { min: 0, max: 100 },
        },
    });

    const secondLeadIdRef = useRef(secondvitalGraphName?.id);
    const bedIdRef = useRef(props.bedId);

    useEffect(() => {
        secondLeadIdRef.current = secondvitalGraphName?.id;
        ecgIIQueue.current.clear();
    }, [secondvitalGraphName]);

    useEffect(() => {
        bedIdRef.current = props.bedId;
    }, [props.bedId]);

    useEffect(() => {
        const ecgListener = (event) => {
            if (!event || event.bedId !== bedIdRef.current) return;
            const wf = event?.eCGWaveform;
            if (!wf) return;

            const ecgIArr = wf[String(ECG_I_LEAD_ID)] || wf[ECG_I_LEAD_ID];
            if (Array.isArray(ecgIArr) && ecgIArr.length) {
                ecgIQueue.current.push(
                    ecgIArr.map((v) => ({ value: v, color: "#00E233" }))
                );
            }

            const secondLeadKey = secondLeadIdRef.current;
            const ecgIIArr = wf[String(secondLeadKey)] || wf[secondLeadKey];
            if (Array.isArray(ecgIIArr) && ecgIIArr.length) {
                ecgIIQueue.current.push(
                    ecgIIArr.map((v) => ({ value: v, color: "#00E233" }))
                );
            }
        };

        const secondaryListener = (event) => {
            if (!event || event.bedId !== bedIdRef.current) return;
            const wf = event?.secondaryWaveforms;
            if (!wf) return;

            const respArr = wf[String(RESP_OBJ.id)] || wf[RESP_OBJ.id];
            if (Array.isArray(respArr) && respArr.length) {
                respQueue.current.push(
                    respArr.map((v) => ({ value: Math.max(-120, Math.min(120, v)), color: "#FCC331" }))
                );
            }

            const plethArr = wf[String(PLETH_OBJ.id)] || wf[PLETH_OBJ.id];
            if (Array.isArray(plethArr) && plethArr.length) {
                plethQueue.current.push(
                    plethArr.map((v) => ({ value: v, color: "#3ADAE5" }))
                );
            }
        };

        setMonitorEcgWaveformStream(ecgListener);
        setMonitorSecondaryWaveformStream(secondaryListener);

        const consume = (queue) => {
            const size = queue.size();
            if (size > 5000) {
                queue.clear();
                return [];
            }
            let n = 25;
            if (size > 500) n = 100;
            else if (size >= 400) n = 35;
            else if (size >= 300) n = 26;
            return queue.popN(n);
        };

        const draw = () => {
            graphDrawerRef.current.ecgI?.(consume(ecgIQueue.current));
            graphDrawerRef.current.ecgII?.(consume(ecgIIQueue.current));
            graphDrawerRef.current.resp?.(consume(respQueue.current));
            graphDrawerRef.current.pleth?.(consume(plethQueue.current));
        };

        const interval = setInterval(draw, 50);

        return () => {
            clearInterval(interval);
            setMonitorEcgWaveformStream(null);
            setMonitorSecondaryWaveformStream(null);
        };
    }, []);

    useEffect(() => {
        if (bedId !== props.bedId) {
            setBedId(props.bedId);
            ecgIQueue.current.clear();
            ecgIIQueue.current.clear();
            respQueue.current.clear();
            plethQueue.current.clear();
        }
    }, [props.bedId]);

    return (
        <>
            {props.config?.userView === USER_VIEW.DESKTOP && (
                <MonitorChartDesktopView
                    secondvitalGraphName={secondvitalGraphName}
                    setSecondVitalGraphName={setSecondVitalGraphName}
                    secondMoreVitalECG={secondMoreVitalECG}
                    setSecondMoreVitalECG={setSecondMoreVitalECG}
                    dataRange={dataRange}
                    graphDrawerRef={graphDrawerRef}
                    canvasSize={canvasSize}
                />
            )}

            {props.config?.userView === USER_VIEW.MOBILE && (
                <MonitorChartMobileView
                    secondvitalGraphName={secondvitalGraphName}
                    setSecondVitalGraphName={setSecondVitalGraphName}
                    secondMoreVitalECG={secondMoreVitalECG}
                    setSecondMoreVitalECG={setSecondMoreVitalECG}
                    dataRange={dataRange}
                    graphDrawerRef={graphDrawerRef}
                    canvasSize={canvasSize}
                />
            )}
        </>
    );
};


const mapStateToProps = (state) => ({
    config: state.configReducer.config,
});

export default connect(mapStateToProps, null)(MonitorChart);
